import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { NavLink, useNavigate } from "react-router-dom";
import SkeletonCardLoad from "../../../components/SkeletonCardLoad";
import { DecisionTable, EntryMode, PutDecisionTable, PutDistanceBand } from "../../../models/DecisionTable";
import msalRestClient from "../../../services/clients/MsalRestClient";
import DecisionTableEntry from "./DecisionTableEntry";

const DecisionTableEdit = () => {

    // #region Init States

    let initTable: PutDecisionTable = {
        id           : -1,
        name         : '',
        description  : '',
        distanceBands: []
    }

    // #endregion

    // #region Status & Hooks

    const { id } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [table, setTable]   = useState<PutDecisionTable>(initTable);
    const [errors, setErrors] = useState<String[]>([]);

    const navigate = useNavigate();

    // #endregion

    useEffect(() => { fetchFromApi(); }, []);

    // #region Functions

    /**
     * TBD ...
     */
    const fetchFromApi = () => {

        setIsLoading(true);

        msalRestClient.get<DecisionTable>("/api/tables/" + id)
            .then(response => {
                if (response.status === 200) {
                    setTable({
                        id            : response.data.id,
                        description   : response.data.description,
                        name          : response.data.name,
                        distanceBands : response.data.distanceBands.map(x => ({ id: x.id, amount: x.amount, minValue: x.minValue, maxValue: x.maxValue, mode: EntryMode.Display }))
                    })
                }
            })
            .finally(() => { setIsLoading(false); })
    }

    /**
     * Renders error Pane that display a list of error to user.
     * @returns
     */
    const renderErrorPane = () => {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="alert alert-danger">
                        <p className="fw-bold mb-0">Whoops! There were some problems with your input.</p>
                        <ul className="mb-0">
                            {
                                errors.map((value, index) => {
                                    return (
                                        <li key={"err_" + index}>{value}</li>
                                    )
                                })
                            }
                        </ul>
                    </div>
                </div>
            </div>
        )
    }

    /**
     * TBD ...
     */
    const onNewEntryClick = () => {

        let minValue = table.distanceBands.length > 0 ? table.distanceBands.reduce((max, x) => Math.max(max, x.maxValue), -Infinity) + 1 : 400;

        let newEntry: PutDistanceBand = {
            id       : 0,
            minValue : minValue,
            maxValue : 0,
            amount   : 0,
            mode     : EntryMode.Create
        }

        setTable(x => ({ ...x, distanceBands: [...x.distanceBands, newEntry] }));
    }

    /**
     * TBD ...
     */
    const onSubmitEntry = (entry: PutDistanceBand, index: number) => {

        let distances = table.distanceBands.map((item, i) => (i === index ? entry : item));
        setTable(x => ({ ...x, distanceBands: distances }));
    }

    /**
     * TBD ...
     * @param index
     */
    const onDeleteEntry = (index: number) => {

        let distances = table.distanceBands.filter((x, i) => i !== index);
        setTable(x => ({ ...x, distanceBands: distances }));
    }

    /**
     * TBD ...
     */
    async function onSubmit(): Promise<void> {

        msalRestClient.put<DecisionTable>("/api/tables", table)
            .then(response => {
                if (response.status === 200) { navigate("/admin/distances"); }
            })
            .catch((err: AxiosError) => {
                if (err.response!.status === 422) {
                    if (err.response?.data && (err.response.data as string)) {
                        setErrors([err.response.data as string]);
                    }
                }
            })
    }

    // #endregion

    return (
        <main className="container mb-4">

            <div className="row mb-2">
                <div className="col-12 text-end">
                    <NavLink to="/admin/distances" className="btn btn-danger">
                        <i className="bi bi-x-lg" />
                    </NavLink>
                </div>
            </div>

            <div className="row">
                <div className="col-12">

                    <div className="card">
                        <div className="card-body">

                            {
                                isLoading ? <SkeletonCardLoad cardsCount={1} />
                                    :
                                    <>
                                        {(errors.length > 0) && renderErrorPane()}

                                        <div className="form-group col-md-12">
                                            <label className="fw-bold" htmlFor="tableName">Tablename</label>
                                            <input type="text" className="form-control" id="tableName" name="tableName" value={table.name} onChange={e => setTable(x => ({ ...x, name: e.target.value }))} />
                                        </div>

                                        <div className="form-group col-md-12">
                                            <label className="fw-bold" htmlFor="description">Description</label>
                                            <textarea cols={1} rows={5} value={table.description} className="form-control" id="description" name="description" onChange={e => setTable(x => ({ ...x, description: e.target.value }))} />
                                        </div>

                                        <div className="col-12">

                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th colSpan={5}>
                                                            <button className="btn btn-success" onClick={e => onNewEntryClick()}>
                                                                <i className="bi bi-plus-lg me-1"></i>Add new Distance
                                                            </button>
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th>Min Value</th>
                                                        <th>Max Value</th>
                                                        <th>Amount</th>
                                                        <th>
                                                            { /* Empty for controls */}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        table.distanceBands.map((value, index) => {
                                                            return <DecisionTableEntry index={index} value={value} onConfirm={onSubmitEntry} onDelete={onDeleteEntry} />
                                                        })
                                                    }
                                                </tbody>
                                            </table>

                                        </div>
                                    </>
                            }

                        </div>
                        <div className="card-footer text-end">
                            <button className="btn btn-success" onClick={onSubmit}><i className="bi bi-check-lg me-1"></i>Validate and Create</button>
                        </div>
                    </div>

                </div>
            </div>

        </main>        
    )
}

export default DecisionTableEdit;